import { useState, useEffect } from 'react';
import { useAuth } from './useAuth';
import { supabase } from '@/integrations/supabase/client';

export type SubscriptionTier = 'economy' | 'premium_economy' | 'first_class';

interface SubscriptionData {
  subscribed: boolean;
  subscription_tier: SubscriptionTier;
  subscription_end?: string;
}

export function useSubscription() {
  const { user } = useAuth();
  const [subscription, setSubscription] = useState<SubscriptionData>({
    subscribed: false,
    subscription_tier: 'economy'
  });
  const [loading, setLoading] = useState(true);

  const checkSubscription = async () => {
    if (!user) {
      setSubscription({ subscribed: false, subscription_tier: 'economy' });
      setLoading(false);
      return;
    }

    try {
      // Check for active boost passes first
      const { data: boostPasses } = await supabase
        .from('boost_passes')
        .select('*')
        .eq('user_id', user.id)
        .eq('is_active', true)
        .gt('expires_at', new Date().toISOString())
        .order('expires_at', { ascending: false });

      // If user has an active boost pass, use that tier
      if (boostPasses && boostPasses.length > 0) {
        // Find the highest tier boost pass (first_class > premium_economy)
        const firstClassBoost = boostPasses.find(bp => bp.feature_name === 'first_class');
        const premiumBoost = boostPasses.find(bp => bp.feature_name === 'premium_economy');
        
        const activeBoost = firstClassBoost || premiumBoost;
        if (activeBoost) {
          setSubscription({
            subscribed: true,
            subscription_tier: activeBoost.feature_name as SubscriptionTier,
            subscription_end: activeBoost.expires_at
          });
          setLoading(false);
          return;
        }
      }

      // Check for redeemed gift subscriptions that are still active
      const { data: giftSubscriptions } = await supabase
        .from('gift_subscriptions')
        .select('*')
        .eq('redeemed_by_user_id', user.id)
        .eq('is_redeemed', true)
        .gt('expires_at', new Date().toISOString())
        .order('expires_at', { ascending: false });

      if (giftSubscriptions && giftSubscriptions.length > 0) {
        // Find the highest tier gift (first_class > premium_economy)
        const firstClassGift = giftSubscriptions.find(g => g.tier === 'first_class');
        const premiumGift = giftSubscriptions.find(g => g.tier === 'premium_economy');
        
        const activeGift = firstClassGift || premiumGift;
        if (activeGift) {
          setSubscription({
            subscribed: true,
            subscription_tier: activeGift.tier as SubscriptionTier,
            subscription_end: activeGift.expires_at
          });
          setLoading(false);
          return;
        }
      }

      // Then check regular subscription from database
      const { data: localData } = await supabase
        .from('subscribers')
        .select('*')
        .eq('user_id', user.id)
        .maybeSingle();

      if (localData) {
        // Honor active free trial
        const trialActive =
          (localData as any).is_trial === true &&
          (localData as any).trial_ends_at &&
          new Date((localData as any).trial_ends_at) > new Date();

        if (trialActive) {
          setSubscription({
            subscribed: true,
            subscription_tier: 'premium_economy',
            subscription_end: (localData as any).trial_ends_at,
          });
        } else {
          setSubscription({
            subscribed: localData.subscribed,
            subscription_tier: localData.subscription_tier || 'economy',
            subscription_end: localData.subscription_end
          });
        }
      } else {
        // No subscriber row yet — default to free tier. Rows are created
        // server-side (Stripe webhook / trial function) only.
        setSubscription({ subscribed: false, subscription_tier: 'economy' });
      }

    } catch (error) {
      console.error('Error checking subscription:', error);
      setSubscription({ subscribed: false, subscription_tier: 'economy' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (user) {
      checkSubscription();
    } else {
      setSubscription({ subscribed: false, subscription_tier: 'economy' });
      setLoading(false);
    }
  }, [user]);

  const hasFeature = (feature: string): boolean => {
    const { subscription_tier } = subscription;
    
    // Economy features (available to all tiers)
    if (feature === 'basic_packing' || feature === 'currency_converter' || 
        feature === 'basic_checklist' || feature === 'basic_etiquette' ||
        feature === 'data_export' || feature === 'data_import' || feature === 'auto_save') {
      return true;
    }
    
    // Premium Economy features
    if (subscription_tier === 'premium_economy' || subscription_tier === 'first_class') {
      if (feature === 'advanced_packing' || feature === 'sim_recommendations' ||
          feature === 'transport_finder' || feature === 'language_phrases' ||
          feature === 'health_tracking' || feature === 'enhanced_notifications' ||
          feature === 'travel_timeline' || feature === 'budget_tracking' ||
          feature === 'document_alerts' || feature === 'expense_analytics') {
        return true;
      }
    }
    
    // First Class exclusive features (includes all Premium Economy features above)
    if (subscription_tier === 'first_class') {
      if (feature === 'calendar_sync' || feature === 'comprehensive_health' ||
          feature === 'pro_transport' || feature === 'custom_templates' ||
          feature === 'personalized_notifications' || feature === 'expense_tracking' ||
          feature === 'translation' || feature === 'ai_assistant' || 
          feature === 'seasonal_recommendations' || feature === 'weather_notifications' ||
          feature === 'post_trip_followup' || feature === 'split_bills' ||
          feature === 'offline_mode' || feature === 'share_templates' ||
          feature === 'multi_city_packing' || feature === 'advanced_analytics' ||
          feature === 'group_progress_sharing') {
        return true;
      }
    }
    
    return false;
  };

  return {
    subscription,
    loading,
    hasFeature,
    refreshSubscription: checkSubscription
  };
}